import {
  CopyButton,
  DataTable,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { IP_SUBNET_CALCULATOR_STRINGS } from "@/components/tools/ip-subnet-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Block,
  binaryIPv4,
  bitsOf,
  classOf,
  expandIPv6,
  exponentOf,
  formatAddress,
  formatIPv4,
  type HostRange,
  mappedIPv4,
  planSplit,
  prefixMask,
  type RangeHit,
  type RangeId,
  type Role,
  type SplitMode,
  splitBlock,
  wildcardMask,
} from "@/lib/tool-kit/cs-tools/ip";

export type Strings = typeof IP_SUBNET_CALCULATOR_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const PLAIN: Intl.NumberFormatOptions = {
  useGrouping: false,
  maximumFractionDigits: 0,
};
const V4_ROWS = 64;
const V6_ROWS = 32;

const RANGE_KEYS: Record<RangeId, keyof Strings> = {
  thisNetwork: "rangeThisNetwork",
  private: "rangePrivate",
  sharedSpace: "rangeSharedSpace",
  loopback: "rangeLoopback",
  linkLocal: "rangeLinkLocal",
  ietfProtocol: "rangeIetfProtocol",
  testNet: "rangeTestNet",
  relay6to4: "rangeRelay6to4",
  benchmarking: "rangeBenchmarking",
  multicast: "rangeMulticast",
  reserved: "rangeReserved",
  limitedBroadcast: "rangeLimitedBroadcast",
  unspecified: "rangeUnspecified",
  mapped: "rangeMapped",
  nat64: "rangeNat64",
  nat64Local: "rangeNat64Local",
  discard: "rangeDiscard",
  teredo: "rangeTeredo",
  documentation: "rangeDocumentation",
  sixToFour: "rangeSixToFour",
  global: "rangeGlobal",
  uniqueLocal: "rangeUniqueLocal",
  siteLocal: "rangeSiteLocal",
};

function bigText(value: bigint, locale: string): string {
  return value.toLocaleString(locale);
}

function cidrText(block: Block): string {
  return `${formatAddress(block.family, block.network)}/${block.prefix}`;
}

function rangeText(
  first: bigint,
  last: bigint,
  family: Block["family"]
): string {
  const a = formatAddress(family, first);
  return first === last ? a : `${a} – ${formatAddress(family, last)}`;
}

const ROLE_KEYS = {
  network: "roleNetwork",
  broadcast: "roleBroadcast",
  host: "roleHost",
} as const;

export function SummaryStats({
  block,
  range,
  role,
  strings,
  locale,
}: {
  block: Block;
  range: HostRange;
  role: Role;
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  return (
    <StatGrid>
      <StatCard emphasis label={strings.statNetwork} value={cidrText(block)} />
      <StatCard
        label={strings.statHosts}
        value={bigText(range.usable, locale)}
      />
      <StatCard
        label={strings.statAddresses}
        value={bigText(block.size, locale)}
      />
      {block.family === 4 ? (
        <StatCard label={strings.statRole} value={strings[ROLE_KEYS[role]]} />
      ) : null}
    </StatGrid>
  );
}

function v4Rows(
  address: bigint,
  block: Block,
  range: HostRange,
  strings: Strings,
  locale: string
): (readonly [string, string])[] {
  return [
    [strings.factAddress, formatIPv4(address)],
    [strings.factPrefix, `/${block.prefix}`],
    [strings.factMask, formatIPv4(prefixMask(4, block.prefix))],
    [strings.factWildcard, formatIPv4(wildcardMask(4, block.prefix))],
    [strings.factNetwork, formatIPv4(block.network)],
    [
      strings.factBroadcast,
      range.broadcast === null
        ? strings.noBroadcast
        : formatIPv4(range.broadcast),
    ],
    [strings.factFirst, formatIPv4(range.first)],
    [strings.factLast, formatIPv4(range.last)],
    [strings.factSize, bigText(block.size, locale)],
    [strings.factUsable, bigText(range.usable, locale)],
    [strings.factClass, t(strings.classValue, { letter: classOf(address) })],
  ];
}

function v6Rows(
  address: bigint,
  block: Block,
  range: HostRange,
  strings: Strings,
  fmt: Fmt,
  locale: string
): (readonly [string, string])[] {
  const rows: (readonly [string, string])[] = [
    [strings.factAddress, formatAddress(6, address)],
    [strings.factExpanded, expandIPv6(address)],
  ];
  const mapped = mappedIPv4(address);
  if (mapped !== null) {
    rows.push([strings.factMapped, `::ffff:${mapped}`]);
  }
  rows.push(
    [strings.factPrefix, `/${block.prefix}`],
    [strings.factNetwork, cidrText(block)],
    [strings.factFirstV6, formatAddress(6, range.first)],
    [strings.factLastV6, formatAddress(6, range.last)],
    [
      strings.factSize,
      `2${superscriptDigits(exponentOf(block.size))} = ${bigText(block.size, locale)}`,
    ],
    [
      strings.factSubnets64,
      block.prefix <= 64
        ? bigText(BigInt(2) ** BigInt(64 - block.prefix), locale)
        : strings.smallerThan64,
    ],
    [strings.factHostBits, fmt(block.hostBits, PLAIN)]
  );
  return rows;
}

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";

function superscriptDigits(n: number): string {
  return String(n)
    .split("")
    .map((d) => SUPERSCRIPTS[Number(d)] ?? d)
    .join("");
}

export function FactsSection({
  address,
  block,
  range,
  strings,
  fmt,
  locale,
}: {
  address: bigint;
  block: Block;
  range: HostRange;
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  const rows =
    block.family === 4
      ? v4Rows(address, block, range, strings, locale)
      : v6Rows(address, block, range, strings, fmt, locale);
  const notes: string[] = [];
  if (block.family === 4 && block.prefix === 31) {
    notes.push(strings.note31);
  }
  if (block.family === 4 && block.prefix === 32) {
    notes.push(strings.note32);
  }
  if (block.family === 6 && block.prefix === 127) {
    notes.push(strings.note127);
  }
  if (block.family === 6 && block.prefix > 64 && block.prefix < 127) {
    notes.push(strings.noteLongPrefix);
  }
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.factsHeading}</h3>
      <DataTable
        caption={strings.factsCaption}
        head={[strings.colProperty, strings.colValue]}
        rows={rows.map(([label, value]) => [label, value])}
      />
      {notes.map((note) => (
        <p className="text-muted-foreground text-sm" key={note}>
          {note}
        </p>
      ))}
      {block.family === 4 ? (
        <BinaryTable address={address} block={block} strings={strings} />
      ) : null}
    </section>
  );
}

function BinaryCell({ dotted, prefix }: { dotted: string; prefix: number }) {
  let seen = 0;
  let split = dotted.length;
  for (let i = 0; i < dotted.length; i++) {
    if (seen === prefix) {
      split = i;
      break;
    }
    if (dotted[i] !== ".") {
      seen += 1;
    }
  }
  let head = dotted.slice(0, split);
  let tail = dotted.slice(split);
  if (head.endsWith(".")) {
    tail = `.${tail}`;
    head = head.slice(0, -1);
  }
  return (
    <td className={`${TD} whitespace-nowrap font-mono`}>
      <span className="font-semibold text-foreground">{head}</span>
      <span className="text-muted-foreground">{tail}</span>
    </td>
  );
}

function BinaryTable({
  address,
  block,
  strings,
}: {
  address: bigint;
  block: Block;
  strings: Strings;
}) {
  const rows = [
    [strings.rowAddress, binaryIPv4(address)],
    [strings.rowMask, binaryIPv4(prefixMask(4, block.prefix))],
    [strings.rowNetwork, binaryIPv4(block.network)],
  ] as const;
  return (
    <div className="space-y-2 pt-2">
      <h3 className={WIDGET_H3}>{strings.binaryHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <caption className="sr-only">{strings.binaryCaption}</caption>
          <tbody>
            {rows.map(([label, dotted]) => (
              <tr key={label}>
                <th className={`${TH} whitespace-nowrap`} scope="row">
                  {label}
                </th>
                <BinaryCell dotted={dotted} prefix={block.prefix} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-muted-foreground text-sm">
        {t(strings.binaryLegend, { prefix: block.prefix })}
      </p>
    </div>
  );
}

export function RangesTable({
  hits,
  strings,
}: {
  hits: RangeHit[];
  strings: Strings;
}) {
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.rangesHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.rangesBody}</p>
      {hits.length === 0 ? (
        <p className="text-foreground text-sm">{strings.rangesNone}</p>
      ) : (
        <DataTable
          caption={strings.rangesCaption}
          head={[
            strings.colRange,
            strings.colBlock,
            strings.colRfc,
            strings.colRelation,
          ]}
          rows={hits.map((hit) => [
            strings[RANGE_KEYS[hit.id]],
            hit.cidr,
            hit.rfc,
            hit.relation === "inside" ? strings.relInside : strings.relCovers,
          ])}
        />
      )}
    </section>
  );
}

const HINT_KEYS = {
  prefix: "splitHintPrefix",
  subnets: "splitHintSubnets",
  hosts: "splitHintHosts",
} as const;

export function SplitSection({
  block,
  mode,
  value,
  valueNumber,
  onChange,
  strings,
  fmt,
  locale,
}: {
  block: Block;
  mode: SplitMode;
  value: string;
  valueNumber: number | null;
  onChange: (value: string) => void;
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  const bits = bitsOf(block.family);
  const plan = planSplit(
    block.family,
    block.prefix,
    mode,
    valueNumber ?? Number.NaN
  );
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.splitHeading}</h3>
      <TextField
        hint={t(strings[HINT_KEYS[mode]], {
          min: fmt(block.prefix, PLAIN),
          max: fmt(bits, PLAIN),
        })}
        id="ip-split-value"
        label={strings.splitValueLabel}
        mono
        onChange={onChange}
        value={value}
      />
      {plan.ok ? (
        <SplitTable
          block={block}
          fmt={fmt}
          locale={locale}
          mode={mode}
          plan={plan}
          strings={strings}
        />
      ) : (
        <p className="text-destructive text-sm" role="alert">
          {t(strings[splitErrorKey(plan.error, mode)], {
            prefix: fmt(block.prefix, PLAIN),
            max: fmt(bits, PLAIN),
          })}
        </p>
      )}
    </section>
  );
}

function splitErrorKey(
  error: "invalidNumber" | "tooSmall" | "tooFine",
  mode: SplitMode
): keyof Strings {
  if (error === "invalidNumber") {
    return "errSplitNumber";
  }
  if (error === "tooFine") {
    return "errSplitTooFine";
  }
  return mode === "hosts" ? "errSplitHostsSmall" : "errSplitPrefixSmall";
}

function SplitTable({
  block,
  mode,
  plan,
  strings,
  fmt,
  locale,
}: {
  block: Block;
  mode: SplitMode;
  plan: { newPrefix: number; count: bigint; requested: number | null };
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  const v4 = block.family === 4;
  const rows = splitBlock(block, plan.newPrefix, v4 ? V4_ROWS : V6_ROWS);
  const first = rows[0];
  const sample = first ? first.range : null;
  const hostBits = bitsOf(block.family) - plan.newPrefix;
  const countText = `${bigText(plan.count, locale)}${tPlural(
    strings.subnetsWord,
    locale,
    Number(plan.count)
  )}`;
  const rounded =
    mode === "subnets" &&
    plan.requested !== null &&
    BigInt(plan.requested) !== plan.count;
  return (
    <div className="space-y-3">
      <p className="text-foreground text-sm">
        {t(strings.splitSummary, {
          count: countText,
          prefix: fmt(plan.newPrefix, PLAIN),
          size: bigText(BigInt(2) ** BigInt(hostBits), locale),
          usable: sample ? bigText(sample.usable, locale) : "0",
        })}
        {rounded
          ? ` ${t(strings.splitRounded, {
              requested: fmt(plan.requested ?? 0, PLAIN),
              count: bigText(plan.count, locale),
            })}`
          : ""}
        {BigInt(rows.length) < plan.count
          ? ` ${t(strings.splitLimited, { shown: fmt(rows.length, PLAIN) })}`
          : ""}
      </p>
      <DataTable
        caption={strings.splitCaption}
        head={
          v4
            ? [
                strings.colIndex,
                strings.colNetwork,
                strings.colRangeUsable,
                strings.colBroadcast,
                strings.colUsable,
              ]
            : [strings.colIndex, strings.colNetwork, strings.colRangeAll]
        }
        rows={rows.map((row) => {
          const base = [fmt(row.index, PLAIN), cidrText(row.block)];
          const span = rangeText(row.range.first, row.range.last, block.family);
          if (!v4) {
            return [...base, span];
          }
          return [
            ...base,
            span,
            row.range.broadcast === null
              ? strings.noBroadcast
              : formatAddress(4, row.range.broadcast),
            bigText(row.range.usable, locale),
          ];
        })}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copySubnets}
        text={rows.map((row) => cidrText(row.block)).join("\n")}
      />
    </div>
  );
}
